/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class AccountChurnService {

    /**
     * What each Hire churn step would change on the account
     * Reads only. Lists every churn step in the contract, whether Hire has built it, whether it still has work, and what that work is.
     * @param accountExternalId Hire account external ID; must be the account the token names
     * @param authorization Bearer <WX system JWT carrying hireapi:account-churn>
     * @returns any the steps and their pending work
     * @throws ApiError
     */
    public static getInternalApiWxAccountChurnPreview(
        accountExternalId: string,
        authorization: string,
    ): CancelablePromise<{
        account_external_id: string;
        steps: Array<{
            step: 'account_inactive' | 'twilio_release' | 'bird_release' | 'wx_bird_release' | 'other_sms_report' | 'i9_everify' | 'sourcing_purchases' | 'posthire_rules' | 'scim' | 'translations' | 'funnels_inactive' | 'partners' | 'retention_report';
            /**
             * Never part of a default selection
             */
            opt_in: boolean;
            needed: boolean;
            detail: string;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/account_churn/{account_external_id}/preview',
            path: {
                'account_external_id': accountExternalId,
            },
            headers: {
                'Authorization': authorization,
            },
            errors: {
                422: `a parent account is refused, never cascaded`,
            },
        });
    }

    /**
     * Run one Hire churn step for a WX churn run
     * Queues the step and answers 202. One row per account, run and step: a repeated request returns that row, a completed step answers 200 without running again, and a failed step, or one queued or running untouched for 15 minutes, is queued again.
     * @param accountExternalId Hire account external ID; must be the account the token names
     * @param step
     * @param authorization Bearer <WX system JWT carrying hireapi:account-churn>
     * @param requestBody
     * @returns any the completed row; the step does not run again
     * @throws ApiError
     */
    public static postInternalApiWxAccountChurnSteps(
        accountExternalId: string,
        step: 'account_inactive' | 'twilio_release' | 'bird_release' | 'wx_bird_release' | 'other_sms_report' | 'i9_everify' | 'sourcing_purchases' | 'posthire_rules' | 'scim' | 'translations' | 'funnels_inactive' | 'partners' | 'retention_report',
        authorization: string,
        requestBody?: {
            wx_run_uuid: string;
            /**
             * WX user who executed the churn; stamped on the step row and the audit trail
             */
            operator_user_uuid: string;
            /**
             * Required for wx_bird_release, ignored otherwise: the Bird numbers on the WX Sender. Sent on every request and never stored; the row keeps ids and the last 4 digits.
             */
            numbers?: Array<{
                workspace_id: string;
                number_id: string;
                number: string;
            }>;
        },
    ): CancelablePromise<{
        step: 'account_inactive' | 'twilio_release' | 'bird_release' | 'wx_bird_release' | 'other_sms_report' | 'i9_everify' | 'sourcing_purchases' | 'posthire_rules' | 'scim' | 'translations' | 'funnels_inactive' | 'partners' | 'retention_report';
        wx_run_uuid: string;
        /**
         * WX user who last asked for the step
         */
        operator_user_uuid: string;
        status: 'queued' | 'running' | 'completed' | 'failed';
        /**
         * What the step changed: ids it touched and previous values
         */
        result: any;
        error?: string | null;
        updated_at?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/wx/account_churn/{account_external_id}/steps/{step}',
            path: {
                'account_external_id': accountExternalId,
                'step': step,
            },
            headers: {
                'Authorization': authorization,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                403: `the path account is not the token account`,
                422: `refused: has_sub_accounts, open_everify_cases, no_wx_bird_workspace, wx_bird_workspace_mismatch, or a malformed wx_run_uuid, operator_user_uuid or numbers`,
            },
        });
    }

    /**
     * Read the outcome of one churn step for a WX churn run
     * @param accountExternalId Hire account external ID; must be the account the token names
     * @param step
     * @param authorization Bearer <WX system JWT carrying hireapi:account-churn>
     * @param wxRunUuid
     * @returns any the step row for the run
     * @throws ApiError
     */
    public static getInternalApiWxAccountChurnSteps(
        accountExternalId: string,
        step: 'account_inactive' | 'twilio_release' | 'bird_release' | 'wx_bird_release' | 'other_sms_report' | 'i9_everify' | 'sourcing_purchases' | 'posthire_rules' | 'scim' | 'translations' | 'funnels_inactive' | 'partners' | 'retention_report',
        authorization: string,
        wxRunUuid: string,
    ): CancelablePromise<{
        step: 'account_inactive' | 'twilio_release' | 'bird_release' | 'wx_bird_release' | 'other_sms_report' | 'i9_everify' | 'sourcing_purchases' | 'posthire_rules' | 'scim' | 'translations' | 'funnels_inactive' | 'partners' | 'retention_report';
        wx_run_uuid: string;
        /**
         * WX user who last asked for the step
         */
        operator_user_uuid: string;
        status: 'queued' | 'running' | 'completed' | 'failed';
        /**
         * What the step changed: ids it touched and previous values
         */
        result: any;
        error?: string | null;
        updated_at?: string;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/wx/account_churn/{account_external_id}/steps/{step}',
            path: {
                'account_external_id': accountExternalId,
                'step': step,
            },
            headers: {
                'Authorization': authorization,
            },
            query: {
                'wx_run_uuid': wxRunUuid,
            },
            errors: {
                404: `no row for that run and step on this account`,
            },
        });
    }

    /**
     * Which WX Sender Bird numbers wx_bird_release would skip, and why
     * Reads only. A POST because the body carries phone numbers. wx_bird_release skips a number Hire holds or an earlier run settled, and cancels the rest.
     * @param accountExternalId Hire account external ID; must be the account the token names
     * @param authorization Bearer <WX system JWT carrying hireapi:account-churn>
     * @param requestBody
     * @returns any why the step skips each number, or null when it cancels it
     * @throws ApiError
     */
    public static postInternalApiWxAccountChurnStepsWxBirdReleaseOwnership(
        accountExternalId: string,
        authorization: string,
        requestBody?: {
            /**
             * The Bird numbers on the WX Sender, validated as for wx_bird_release. Never stored.
             */
            numbers: Array<{
                workspace_id: string;
                number_id: string;
                number: string;
            }>;
        },
    ): CancelablePromise<{
        numbers: Array<{
            number_id: string;
            /**
             * A Hire SMS number row holds it, a bird_release step detached it, an earlier wx_bird_release cancelled it or found it gone, or null when the step cancels it
             */
            owned_by: 'hire_row' | 'bird_release' | 'wx_bird_release' | null;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/wx/account_churn/{account_external_id}/steps/wx_bird_release/ownership',
            path: {
                'account_external_id': accountExternalId,
            },
            headers: {
                'Authorization': authorization,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `refused: has_sub_accounts, no_wx_bird_workspace, wx_bird_workspace_mismatch, or malformed numbers`,
            },
        });
    }

}